import { useState, useEffect, type ReactNode } from "react";
import { CurrentUserContext, type CurrentUser } from "@/hooks/use-current-user";
import { useAuth } from "@/hooks/use-auth";
import { apiClient } from "@/lib/api-client";
import { Skeleton } from "@/components/ui/skeleton";

interface CurrentUserProviderProps {
  children: ReactNode;
}

export function CurrentUserProvider({ children }: CurrentUserProviderProps) {
  const { user: msalUser } = useAuth();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!msalUser?.email) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchCurrentUser() {
      try {
        // Fetch users filtered by the authenticated user's email
        const { data } = await apiClient.GET("/v1/users", {
          params: { query: { limit: 100 } },
        });
        if (!cancelled && data) {
          const users = "data" in data ? data.data : [];
          // Find the user matching the MSAL email
          const matchingUser = users?.find(
            (u: { email: string }) => u.email.toLowerCase() === msalUser!.email.toLowerCase()
          );
          if (matchingUser) {
            setCurrentUser({
              id: matchingUser.id,
              name: matchingUser.name,
              email: matchingUser.email,
              role: matchingUser.role as CurrentUser["role"],
              teamId: matchingUser.team_id,
              departmentHead: matchingUser.department_head ?? false,
              familiarity: matchingUser.familiarity ?? "developing",
            });
          }
        }
      } catch {
        // API not available — currentUser remains null
        // Admin nav will be hidden (safe default)
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void fetchCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [msalUser?.email]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--background))]">
        <div className="space-y-4 p-6 text-center">
          <Skeleton className="mx-auto h-8 w-48" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <CurrentUserContext.Provider value={{ currentUser, isLoading }}>
      {children}
    </CurrentUserContext.Provider>
  );
}
